import AuthButton from "../LogInPage/LoginButton";
import { Chart as ChartJS,CategoryScale,LinearScale,PointElement,LineElement,Title,Tooltip,Legend,BarElement,BarController } from "chart.js";
import { useEffect, useState } from "react";
import { useGetWidgetDataQuery } from "../../redux/API/Login/loginApi";
import DomainWidget from "./Widgets/DomainWidget";
import ScoreWidget from "./Widgets/ScoreWidget";
import TimeWidget from "./Widgets/TimeWidget";
import { AssessmentTabProps, TestResult, TestTimeData } from "./Widgets/dataTypes";
import TopicTimeBreakdown from "./Widgets/TopicTimeBreakdown";
import UserScoreWidget from "./Widgets/UserScoreWidget";
import ScaledScoreWidget from "./Widgets/ScaledScoreWidget";
import { getItem } from "../../utils/token";
import FullMarksLoader from "../../components/ReUsableComponents/FullMarksLoader";

ChartJS.register(CategoryScale,LinearScale,PointElement,LineElement,BarElement, BarController,Title,Tooltip,Legend);

interface TestCardProps {
  icon: React.ReactNode;
  heading: string;
  btnTitile: string;
  btnIcon: React.ReactNode;
  onClickHandler: () => void;
  btnDisabled?: boolean;
  tooltipPreview?: boolean;
}

interface DashboardScoreCardProps {
  headerTitle: string;
  footerTitle: string;
  content: JSX.Element;
}

export const TestCards = ({
  icon,
  heading,
  btnIcon,
  btnTitile,
  onClickHandler,
  btnDisabled,
  tooltipPreview,
}: TestCardProps) => {
  return (
    <div className="flex w-64 p-6 flex-col items-center gap-9 shrink-0 self-stretch rounded-xl shadow-test-card">
      <div className="flex flex-col items-center gap-3 self-stretch">
        <div className="w-[70px] h-[70px]">{icon}</div>
        <h3 className="font-gully text-3xl font-bold text-center">{heading}</h3>
      </div>
      <div className="w-full flex justify-center relative group">
        <AuthButton
          text={btnTitile}
          icon={btnIcon}
          iconPosition="left"
          onClick={onClickHandler}
          loader={false}
          disabled={btnDisabled ?? false}
        />
        {tooltipPreview && (
          <div className="absolute w-72 top-8 left-[19rem] font-gully transform -translate-x-1/2 mb-3 px-3 py-6 text-base text-center font-normal text-[#646464] bg-white rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <p>Hey! You already have a Full Practice Test in progress.</p>
            <p className="mt-3">
              You can start a New Full Practice Test once you have completed it
              or discarded it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const DashboardScoreCard = ({
  headerTitle,
  footerTitle,
  content,
}: DashboardScoreCardProps) => {
  return (
    <div className="flex w-[30%] p-6 flex-col items-center rounded-xl justify-between shrink-0 self-stretch shadow-test-card">
      <h4 className="text-center font-gully text-[21px] font-medium">
        {headerTitle}
      </h4>
      {content}
      <p className="text-center font-gully text-lg font-normal">
        {footerTitle}
      </p>
    </div>
  );
};

export const DashboardWidgets = ({studentId,testId}:AssessmentTabProps) => {
  const [message, setMessage] = useState("");
  const queryArgs = studentId ? {studentId : studentId} : testId ? {testId : testId} : {};
  const {
    data: widgetData,
    isLoading: widgetLoading,
    isSuccess: widgetSuccess,
    isError: widgetError,
  } = useGetWidgetDataQuery(queryArgs, { refetchOnMountOrArgChange: true,skip : getItem('userRole') === 'PARENT' && Object.keys(queryArgs).length === 0 });

  const isParent = getItem("userRole") === "PARENT";

  const getMessage = () => {
    if (widgetLoading) {
      return "Loading...";
    }
    if (widgetError) {
      return "Apologies, there's an issue on our end. Please try again later.";
    }
    if (Object.keys(widgetData?.data || {}).length === 0 || widgetData?.data?.avgtimepertest.length === 0 || widgetData?.data?.overalltestprogress.length === 0 || widgetData?.data?.performancebydomain.length === 0) {
      return isParent  ? "It looks like the student hasn’t completed any tests yet." : "Take a full practice test to see how you're doing!";
    }
    return "";
  };

  useEffect(() => {
    setMessage(getMessage());
  }, [widgetData, widgetLoading, widgetError, widgetSuccess]);

  const isBlurred = message !== "";

  const sortedScoreData = Array.isArray(widgetData?.data?.overalltestprogress)
  ? [...widgetData?.data?.overalltestprogress].sort((a: TestResult, b: TestResult) =>
      new Date(a.testtakendate).getTime() - new Date(b.testtakendate).getTime()
    )
  : [];

  const sortedTimeData = Array.isArray(widgetData?.data?.avgtimepertest)
  ? [...widgetData?.data?.avgtimepertest].sort((a: TestTimeData, b: TestTimeData) =>
      new Date(a.testtakendate).getTime() - new Date(b.testtakendate).getTime()
    )
  : [];
  
  return (
    <>
      <div className={`w-full flex flex-col gap-3 relative ${
            isBlurred ? "blur-md pointer-events-none" : ""
          }`}>

        {!testId && (<div
          className={`w-full flex items-center justify-between gap-[1%]`}
        >
          <div className="w-[33%] flex justify-between shrink-0 self-stretch">
            <ScoreWidget overallResult={sortedScoreData.filter((item) => !item?.isCustomTest)} scoreType="ScaledScore" />
          </div>
          
          <div className="w-[32%] flex justify-between shrink-0 self-stretch">
            <ScoreWidget overallResult={sortedScoreData} scoreType="RawScore"/>
          </div>

          <div className="w-[33%] flex justify-between shrink-0 self-stretch">
            <TimeWidget timeData={sortedTimeData} />
          </div>
        </div>)}

        {
          testId && !widgetData?.data?.overalltestprogress[0]?.isCustomTest && (
            <div
              className={`w-full flex items-center justify-between gap-[1%]`}
            >
              <ScaledScoreWidget scaledScores={widgetData?.data?.sectionScaledScores} />
            </div>
          )
        }

        <div className="flex justify-between gap-[1%]">
            <div className="w-[49.5%]">
              <TopicTimeBreakdown
                sectionTimeData={widgetData?.data?.domainLevelPerformance?.readingAndWriting}
                module="Reading & Writing"
                widgetType="topicsTime"
              />
            </div>
            <div className="w-[49.5%]">
              <TopicTimeBreakdown
                sectionTimeData={widgetData?.data?.domainLevelPerformance?.readingAndWriting}
                module="Reading & Writing"
                widgetType="topicsSkipped"
              />
            </div>
        </div>

        <div className="flex justify-between gap-[1%]">
          <div className="w-[49.5%]">
            <TopicTimeBreakdown
              sectionTimeData={widgetData?.data?.domainLevelPerformance?.mathematics}
              module="Math"
              widgetType="topicsTime"
            />
          </div>
          
          <div className="w-[49.5%]">
            <TopicTimeBreakdown
              sectionTimeData={widgetData?.data?.domainLevelPerformance?.mathematics}
              module="Math"
              widgetType="topicsSkipped"
            />
          </div>
        </div>

        <div className="flex justify-between items-stretch gap-[1%]">

          <div className="w-[30%]">
            <DomainWidget
              module="Reading & Writing"
              domainResults={widgetData?.data?.domainLevelPerformance?.readingAndWriting}
            />
          </div>
          <div className="w-[69%]">
            <UserScoreWidget
              subDomainData={widgetData?.data?.subDomainLevelScores?.readingAndWriting}
              module="Reading & Writing"
            />
          </div>

        </div>

        <div className="flex h-[640px] justify-between items-stretch gap-[1%]">
          <div className="w-[30%] h-full">
            <DomainWidget
              module="Math"
              domainResults={widgetData?.data?.domainLevelPerformance?.mathematics}
            />
          </div>

           <div className="w-[69%] h-full ">
            <div className="w-full h-full">
              <UserScoreWidget
                subDomainData={widgetData?.data?.subDomainLevelScores?.mathematics}
                module="Math" 
              />
            </div>
          </div>
        </div>


      </div>

      {isBlurred && (
        <div className="absolute inset-0 flex items-center justify-center bg-opacity-70">
          {message === "Loading..." ? (
            <FullMarksLoader width={150} height={150} />
          ) : (
            <p className="font-gully text-3xl font-bold text-center text-[#283044]">
              {message}
            </p>
          )}
        </div>
      )}

    </>
  );
};

